import { cn } from "@omni/utils/styles";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";

import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

export type SwitchStep = {
    value: boolean | string | number | null | undefined;
    label?: string;
    className?: string;
};

export type SwitchFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    disabled?: boolean;
    steps: Array<SwitchStep>;
};

const SwitchField = <T extends FieldValues>({
    name,
    label,
    required,
    disabled,
    steps = []
}: SwitchFieldProps<T>) => {
    const { setValue } = useFormContext<T>();
    const stepLenght = steps.length;

    if (stepLenght === 0) {
        return null;
    }

    const getValue = (value: PathValue<T, Path<T>>) => {
        return steps.findIndex((step) => step.value === value);
    };

    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            required={required}
        >
            {({ field }) => {
                return (
                    <>
                        <div className={cn("tw:flex tw:justify-between")}>
                            {steps.map((step, index) => (
                                <label
                                    key={index}
                                    htmlFor={name}
                                    onClick={() => {
                                        setValue(
                                            name,
                                            steps[index].value as PathValue<
                                                T,
                                                Path<T>
                                            >,
                                            {
                                                shouldTouch: true,
                                                shouldValidate: true
                                            }
                                        );
                                    }}
                                >
                                    {step.label}
                                </label>
                            ))}
                        </div>
                        <Input
                            {...field}
                            type="range"
                            className={cn(
                                steps[getValue(field.value)]?.className
                            )}
                            id={name}
                            min={0}
                            max={stepLenght - 1}
                            step={1}
                            disabled={disabled}
                            value={getValue(field.value)}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                setValue(
                                    name,
                                    steps[value].value as PathValue<T, Path<T>>,
                                    {
                                        shouldTouch: true,
                                        shouldValidate: true
                                    }
                                );
                            }}
                        />
                    </>
                );
            }}
        </BaseField>
    );
};

export { SwitchField };
